import React, { useState } from "react";
import { Check, ChevronsUpDown, AlertTriangle } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/api";

/**
 * ProductoCombobox — typeahead picker para productos.
 *
 * Props:
 *  - value: producto_id (string | null)
 *  - productos: array de productos { id, nombre, precio }
 *  - onChange: (producto) => void
 *  - testid
 *  - placeholder
 *  - review: boolean (style highlight)
 *  - currentName: string (displayed when value is null - raw text)
 */
export default function ProductoCombobox({
  value,
  productos,
  onChange,
  testid,
  placeholder = "Elegí un producto",
  review = false,
  currentName = "",
}) {
  const [open, setOpen] = useState(false);
  const selected = productos.find((p) => p.id === value);
  const displayText = selected ? selected.nombre : currentName || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testid}
          className={`w-full justify-between bg-white h-10 font-normal text-left ${
            review
              ? "border-[#D49A36] ring-1 ring-[#D49A36]/40 text-[#A6751F]"
              : "border-[#E5E0D8] hover:bg-[#FBF8F2] text-[#1A1A1A]"
          } ${!selected && !currentName ? "text-[#8C8C8C]" : ""}`}
        >
          <span className="flex items-center gap-2 flex-1 min-w-0">
            {review && <AlertTriangle className="h-3.5 w-3.5 text-[#D49A36] shrink-0" />}
            <span className="truncate">{displayText}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]"
        align="start"
      >
        <Command shouldFilter={true}>
          <CommandInput placeholder="Buscar producto..." data-testid={testid ? `${testid}-input` : undefined} />
          <CommandList>
            <CommandEmpty>Sin coincidencias.</CommandEmpty>
            <CommandGroup>
              {productos.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.nombre}
                  onSelect={() => {
                    onChange(p);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-opt-${p.id}` : undefined}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value === p.id ? "opacity-100 text-[#C85A32]" : "opacity-0"
                    }`}
                  />
                  <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                    <span className="text-sm font-medium truncate">{p.nombre}</span>
                    <span className="text-[11px] text-[#C85A32] font-mono font-semibold shrink-0">
                      {formatMoney(p.precio)}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
